import React from 'react';
import {
  Container,
  Header,
  Table,
  TextFilter,
  Pagination,
  CollectionPreferences,
  Box,
  SpaceBetween,
  Button,
} from "@cloudscape-design/components";
import { formatDateTime } from '../../utils/insights/dateFormatters';

/**
 * Componente de tabla de llamadas para resúmenes
 */
export default function SummaryTable({
  items,
  loading,
  selectedItems,
  onSelectionChange,
  filteringText,
  onFilteringTextChange,
  currentPageIndex,
  pagesCount,
  onPageChange,
  preferences,
  onPreferencesChange,
  sortingColumn,
  sortingDescending,
  onSortingChange,
  dateRange,
  onViewInsight,
  t
}) {
  // Con paginación backend, solo aplicar filtrado de búsqueda local (no afecta paginación)
  // El backend ya maneja ordenamiento y paginación
  const filteredItems = filteringText
    ? items.filter(
        item =>
          (item.supervisor && item.supervisor.toLowerCase().includes(filteringText.toLowerCase())) ||
          (item.agent && item.agent.toLowerCase().includes(filteringText.toLowerCase())) ||
          (item.jobName && item.jobName.toLowerCase().includes(filteringText.toLowerCase()))
      )
    : items;

  // Paginación backend: usar items directamente del backend
  const paginatedItems = filteredItems;

  // Definición de columnas para la tabla
  const columnDefinitions = [
    {
      id: "timestamp",
      header: t("insights.timestamp"),
      cell: item => formatDateTime(item.timestamp),
      sortingField: "timestamp",
      width: 160
    },
    {
      id: "jobName",
      header: t("insights.callName"),
      cell: item => (
        <div 
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%'
          }}
          title={item.jobName || "-"}
        >
          {item.jobName || "-"}
        </div>
      ),
      sortingField: "jobName",
      width: 350
    },
    {
      id: "supervisor",
      header: t("insights.supervisor"),
      cell: item => item.supervisor || "-",
      sortingField: "supervisor",
      minWidth: 80
    },
    {
      id: "agent",
      header: t("insights.agent"),
      cell: item => item.agent || "-",
      sortingField: "agent",
      minWidth: 80
    },
    {
      id: "actions",
      header: t("insights.actions"),
      cell: item => (
        <Button
          variant="link"
          onClick={() => onViewInsight(item)}
        >
          {t("insights.viewInsight")}
        </Button>
      ),
      width: 120
    }
  ];

  return (
    <Container
      header={
        <Header variant="h2" description={t("insights.summaryCallsDescription")}>
          {t("insights.summaryCalls")}
        </Header>
      }
    >
      <Table
        columnDefinitions={columnDefinitions}
        items={paginatedItems}
        selectedItems={selectedItems}
        onSelectionChange={({ detail }) => onSelectionChange(detail.selectedItems)}
        trackBy="id"
        selectionType="multi"
        ariaLabels={{
          selectionGroupLabel: t("insights.selectionGroupLabel"),
          allItemsSelectionLabel: () => t("insights.selectAll"),
          itemSelectionLabel: ({ selectedItems }, item) => 
            t("insights.selectCall", { jobName: item.jobName })
        }}
        loading={loading}
        loadingText={t("common.loading")}
        wrapLines={preferences.wrapLines}
        resizableColumns
        visibleColumns={preferences.visibleContent}
        sortingColumn={sortingColumn}
        sortingDescending={sortingDescending}
        onSortingChange={onSortingChange}
        filter={
          <TextFilter
            filteringText={filteringText}
            filteringPlaceholder={t("insights.searchCalls")}
            filteringAriaLabel={t("insights.searchCalls")}
            onChange={({ detail }) => onFilteringTextChange(detail.filteringText)}
          />
        }
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            pagesCount={pagesCount}
            onChange={({ detail }) => onPageChange(detail.currentPageIndex)}
            ariaLabels={{
              nextPageLabel: t("common.nextPage"),
              previousPageLabel: t("common.previousPage"),
              pageLabel: pageNumber => `${t("common.page")} ${pageNumber}`
            }}
          />
        }
        preferences={
          <CollectionPreferences
            title={t("contactTable.preferences")}
            confirmLabel={t("contactTable.confirm")}
            cancelLabel={t("contactTable.cancel")}
            preferences={preferences}
            onConfirm={onPreferencesChange}
            pageSizePreference={{
              title: t("contactTable.pageSize"),
              options: [
                { value: 10, label: "10 registros" },
                { value: 20, label: "20 registros" },
                { value: 50, label: "50 registros" }
              ]
            }}
            wrapLinesPreference={{
              label: t("contactTable.wrapLines"),
              description: t("contactTable.wrapLinesDescription")
            }}
            visibleContentPreference={{
              title: t("contactTable.selectVisibleColumns"),
              options: [
                {
                  label: t("contactTable.callListProperties"),
                  options: [
                    { id: "timestamp", label: t("insights.timestamp") },
                    { id: "jobName", label: t("insights.callName") },
                    { id: "supervisor", label: t("insights.supervisor") },
                    { id: "agent", label: t("insights.agent") },
                    { id: "actions", label: t("insights.actions") }
                  ]
                }
              ]
            }}
          />
        }
        empty={
          <Box textAlign="center" color="inherit" padding={{ vertical: "xl" }}>
            <SpaceBetween size="m">
              <div>
                <b>{t("insights.noSummaries")}</b>
              </div>
              {!dateRange && (
                <div>
                  {t("insights.selectDateRangeFirst")}
                </div>
              )}
            </SpaceBetween>
          </Box>
        }
      />
    </Container>
  );
}

